export type BrandId = 'default' | 'dashboard' | 'product-a' | 'product-b';

export enum ThemeMode {
  Light = 'light',
  Dark = 'dark',
  System = 'system',
}

export type ThemeModeValue = 'light' | 'dark' | 'system';
export type ResolvedThemeMode = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'shigo-ui-theme';

export const brands: Record<BrandId, { label: string }> = {
  default: { label: 'Shigo' },
  dashboard: { label: 'Dashboard' },
  'product-a': { label: 'Shigo Product A' },
  'product-b': { label: 'Shigo Product B' },
};

export function applyBrand(brand: BrandId) {
  document.documentElement.dataset['brand'] = brand;
}

export function applyTheme(theme: ThemeModeValue): ResolvedThemeMode {
  const resolvedTheme = resolveTheme(theme);
  document.documentElement.dataset['theme'] = resolvedTheme;
  document.documentElement.style.colorScheme = resolvedTheme;
  syncThemeColor();

  return resolvedTheme;
}

/**
 * Paints the phone's status bar and browser chrome in the app's top surface color. The app's
 * own theme can differ from the OS scheme, so every theme-color tag gets the resolved color.
 */
function syncThemeColor() {
  if (typeof getComputedStyle !== 'function' || !('head' in document)) return;
  const color = getComputedStyle(document.documentElement).getPropertyValue('--card').trim();
  if (!color) return;
  document.head.querySelectorAll('meta[name="theme-color"]').forEach((meta) => {
    meta.setAttribute('content', color);
  });
}

export function setTheme(theme: ThemeModeValue, storage: Storage | null = getBrowserStorage()) {
  const normalizedTheme = normalizeThemeMode(theme);
  storage?.setItem(THEME_STORAGE_KEY, normalizedTheme);
  return applyTheme(normalizedTheme);
}

export function initializeTheme(storage: Storage | null = getBrowserStorage()) {
  const theme = getStoredTheme(storage);
  applyTheme(theme);
  return theme;
}

export function getStoredTheme(storage: Storage | null = getBrowserStorage()): ThemeMode {
  const storedTheme = storage?.getItem(THEME_STORAGE_KEY);

  return isThemeMode(storedTheme) ? storedTheme : ThemeMode.Light;
}

export function resolveTheme(theme: ThemeModeValue): ResolvedThemeMode {
  const normalizedTheme = normalizeThemeMode(theme);

  if (normalizedTheme === ThemeMode.System) {
    return systemPrefersDark() ? ThemeMode.Dark : ThemeMode.Light;
  }

  return normalizedTheme;
}

export function watchSystemTheme(callback: () => void) {
  const media = getSystemThemeMedia();

  if (!media) {
    return () => {};
  }

  media.addEventListener('change', callback);

  return () => {
    media.removeEventListener('change', callback);
  };
}

function isThemeMode(value: string | null | undefined): value is ThemeMode {
  return value === ThemeMode.Light || value === ThemeMode.Dark || value === ThemeMode.System;
}

function normalizeThemeMode(theme: ThemeModeValue): ThemeMode {
  return isThemeMode(theme) ? theme : ThemeMode.Light;
}

function systemPrefersDark() {
  return getSystemThemeMedia()?.matches ?? false;
}

function getSystemThemeMedia() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return null;
  }

  return window.matchMedia('(prefers-color-scheme: dark)');
}

function getBrowserStorage() {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage;
}
